/* eslint-disable @typescript-eslint/member-delimiter-style */
/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react"
import { Navigate, useLocation, useParams } from "react-router-dom"
import { useGetCertificateReportLazyQuery, useGetInstallationInstructionReportLazyQuery, useGetReportLazyQuery } from "../type-and-hooks"

// API configuration interface for different pathname patterns
interface ApiConfig {
  type: string
  query: string
  paramName: string
  errorMessage: string
  variableKey: string
  dataPath: string
}

// API configuration mapping
const API_CONFIGS: Record<string, ApiConfig> = {
  installationInstruction: {
    type: "installationInstruction",
    query: "getInstallationInstructionReport",
    paramName: "instruction",
    errorMessage: "No installation instruction report found with this name!!",
    variableKey: "instruction",
    dataPath: "getInstallationInstructionReport"
  },
  testReport: {
    type: "testReport",
    query: "getReport",
    paramName: "reportNo",
    errorMessage: "No test report found with this name!!",
    variableKey: "reportNo",
    dataPath: "getReport"
  },
  qualityCertificate: {
    type: "qualityCertificate",
    query: "getCertificateReport",
    paramName: "certificateName",
    errorMessage: "No Certificate found with this name!!",
    variableKey: "certificateName",
    dataPath: "getCertificateReport"
  },
  report: {
    type: "report",
    query: "getReport",
    paramName: "reportNo",
    errorMessage: "No report found with this name!!",
    variableKey: "reportNo",
    dataPath: "getReport"
  }
}

export const Report = (): JSX.Element => {
  const { reportNo } = useParams<{ reportNo: string }>()
  const location = useLocation()
  const [hasShownError, setHasShownError] = useState(false)
  const [downloadData, setDownloadData] = useState<{ reportName?: string; reportURL?: string | null } | null>(null)

  // Generic function to get API configuration based on pathname
  const getApiConfig = (pathname: string): ApiConfig => {
    if (pathname.includes("installationInstruction")) {
      return API_CONFIGS.installationInstruction
    }
    if (pathname.includes("testReport")) {
      return API_CONFIGS.testReport
    }
    if (pathname.includes("qualityCertificate")) {
      return API_CONFIGS.qualityCertificate
    }
    // Default case for regular reports
    return API_CONFIGS.report
  }

  const apiConfig = getApiConfig(location.pathname)

  // Individual hooks for each API type
  const [getInstallationInstructionReport, { error: instructionError, data: instructionData, loading: instructionLoading }] =
    useGetInstallationInstructionReportLazyQuery()
  const [getReport, { error: reportError, data: reportData, loading: reportLoading }] = useGetReportLazyQuery()
  const [getCertificateReport, { error: certificateError, data: certificateData, loading: certificateLoading }] = useGetCertificateReportLazyQuery()

  // Get current error and loading state based on API type
  const currentError =
    apiConfig.type === "installationInstruction" ? instructionError : apiConfig.type === "qualityCertificate" ? certificateError : reportError
  const currentLoading =
    apiConfig.type === "installationInstruction" ? instructionLoading : apiConfig.type === "qualityCertificate" ? certificateLoading : reportLoading

  // Generic function to execute the appropriate query
  const executeApiCall = async (): Promise<void> => {
    if (!reportNo) return

    try {
      if (apiConfig.type === "installationInstruction") {
        await getInstallationInstructionReport({ variables: { instruction: reportNo } })
      } else if (apiConfig.type === "qualityCertificate") {
        await getCertificateReport({ variables: { certificateName: reportNo } })
      } else {
        await getReport({ variables: { reportNo } })
      }
    } catch (error) {
      console.error(`Error executing ${apiConfig.query}:`, error)
    }
  }

  useEffect(() => {
    void executeApiCall()
  }, [reportNo, apiConfig])

  useEffect(() => {
    let downloadableReport
    if (apiConfig.type === "installationInstruction") {
      downloadableReport = instructionData?.getInstallationInstructionReport
    } else if (apiConfig.type === "qualityCertificate") {
      downloadableReport = certificateData?.getCertificateReport
    } else {
      downloadableReport = reportData?.getReport
    }

    setDownloadData(downloadableReport ?? null)

    if (downloadableReport?.reportURL) {
      // Create a temporary link element to trigger download
      const link = document.createElement("a")
      link.href = reportData?.getReport?.downloadURL ?? downloadableReport.reportURL
      link.download = downloadableReport.reportName ?? `report-${reportNo}.pdf`
      link.target = "_blank"
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }, [reportNo, apiConfig.type, instructionData, certificateData, reportData])

  // Show error only once and redirect
  if (currentError && !hasShownError) {
    setHasShownError(true)
    // console.log(apiConfig.errorMessage)
    return <Navigate to="/reports/all" replace />
  }

  // Show loading state
  if (currentLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
          fontSize: "18px",
          fontFamily: "Arial, sans-serif"
        }}
      >
        Loading report...
      </div>
    )
  }

  // Show download message when data is available
  if (downloadData?.reportURL) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "61vh",
          fontSize: "18px",
          fontFamily: "Arial, sans-serif"
        }}
      >
        Report "{downloadData.reportName ?? `report-${reportNo}`}" is being downloaded.
      </div>
    )
  }
  // Show error message if no report or URL available
  if (!downloadData?.reportURL) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "61vh",
          fontSize: "18px",
          fontFamily: "Arial, sans-serif"
        }}
      >
        No report found or URL not available for report "{downloadData?.reportName ?? reportNo}".
      </div>
    )
  }

  return <></>
}
